import React from 'react';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

const STEPS = [
  {
    time: 'T+0s',
    title: 'Pick a plan',
    body: `Choose flash volume, validity, and network on the ${siteConfig.brand.name} pricing page. Every tier is multi-chain — you are not locked to TRC-20.`,
  },
  {
    time: 'T+30s',
    title: 'Pay in BTC or USDT',
    body: 'Send to the wallet on the payment screen. On-chain confirmation is auto-detected — no receipt-uploading, no back-and-forth.',
  },
  {
    time: 'T+2m',
    title: 'License key by email',
    body: 'You receive a signed license key, activation link, and network-specific quick-start guide within minutes of the payment landing.',
  },
  {
    time: 'T+5m',
    title: 'Install & activate',
    body: 'Windows 10/11, macOS 12+, or Android 9+. Paste the license, pick a chain, and the desk is live for the plan window.',
  },
  {
    time: 'T+7m',
    title: 'Push your first flash',
    body: 'Enter the receiver, chain, and amount. The balance renders in the target wallet UI and any block explorer that indexes the chosen chain.',
  },
  {
    time: 'Anytime',
    title: 'WhatsApp support',
    body: 'The desk is staffed 24/7 for setup, chain-switch help, and edge cases (wallet updates, RPC issues). Reachable from your license email.',
  },
];

const Section = styled.section`
  padding: 5rem 1rem;
  background: var(--background-dark);
  position: relative;
`;

const Inner = styled.div` max-width: 1160px; margin: 0 auto; `;

const Head = styled.div`
  text-align: center; margin-bottom: 3.5rem;
  .tag {
    display: inline-block;
    padding: 0.35rem 0.9rem; border-radius: 999px;
    background: rgba(0,153,0,0.08); color: var(--primary-color);
    border: 1px solid rgba(0,153,0,0.25);
    font-size: 0.72rem; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase;
    margin-bottom: 1rem;
  }
  h2 {
    font-family: 'Georgia', 'Times New Roman', serif;
    font-size: clamp(1.9rem, 3.4vw, 2.75rem);
    margin: 0 0 0.75rem; color: var(--text-color); letter-spacing: -0.02em;
    span { color: var(--primary-color); font-style: italic; }
  }
  p { color: var(--text-secondary); line-height: 1.7; margin: 0 auto; max-width: 640px; }
`;

const Rail = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 0;
  @media (max-width: 640px) {
    grid-template-columns: 64px 1fr;
  }
`;

const Line = styled.div`
  position: absolute;
  left: 47px; top: 0; bottom: 0;
  width: 2px;
  background: linear-gradient(180deg, transparent 0%, rgba(0,153,0,0.35) 8%, rgba(0,153,0,0.35) 92%, transparent 100%);
  @media (max-width: 640px) { left: 31px; }
`;

const Row = styled.div`
  display: contents;
`;

const TimeCol = styled.div`
  position: relative;
  padding: 0.85rem 0 2.25rem;
  &::before {
    content: '';
    position: absolute; left: 41px; top: 1.5rem;
    width: 14px; height: 14px; border-radius: 50%;
    background: var(--primary-color);
    box-shadow: 0 0 0 4px rgba(0,153,0,0.15), 0 0 14px rgba(0,153,0,0.6);
    z-index: 2;
    @media (max-width: 640px) { left: 25px; }
  }
  .t {
    display: inline-block;
    font-family: 'JetBrains Mono', 'SFMono-Regular', ui-monospace, monospace;
    font-size: 0.72rem; letter-spacing: 0.06em;
    color: var(--primary-color);
    background: rgba(0,153,0,0.08);
    padding: 0.25rem 0.55rem; border-radius: 6px;
    border: 1px solid rgba(0,153,0,0.2);
  }
`;

const Card = styled.div`
  padding: 0.75rem 0 2.25rem 1.5rem;
  h3 {
    color: var(--text-color); font-size: 1.15rem; font-weight: 700;
    margin: 0 0 0.5rem;
    font-family: 'Georgia', 'Times New Roman', serif; letter-spacing: -0.01em;
  }
  p { color: var(--text-secondary); line-height: 1.7; margin: 0; font-size: 0.95rem; }
`;

const Timeline: React.FC = () => (
  <Section id="how-timeline">
    <Inner>
      <Head>
        <div className="tag">The order flow</div>
        <h2>From cart to first flash — <span>seven minutes</span>, on the record.</h2>
        <p>
          A transparent, timestamped walkthrough of what happens after you buy a license from{' '}
          {siteConfig.brand.name}. No dark UX, no waiting on humans in the loop.
        </p>
      </Head>

      <Rail>
        <Line />
        {STEPS.map((s, i) => (
          <Row key={i}>
            <TimeCol><span className="t">{s.time}</span></TimeCol>
            <Card>
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </Card>
          </Row>
        ))}
      </Rail>
    </Inner>
  </Section>
);

export default Timeline;
